import type { Tenant } from '@payload-types';
import {
  sanitizeActiveCampaignAccountId,
  sanitizeGoogleTagId,
  sanitizeGtmId,
  sanitizeMetaPixelId,
  sanitizeOpenAiPixelId,
  storedIdText,
} from '../../../utilities/marketing-tag-ids';

/**
 * Every Marketing Tag the platform can load (see CONTEXT.md), keyed by a
 * stable tag key. It holds no client code, so the layout (a server component)
 * decides from it whether a site has any tag, and the browser clears from it
 * on Withdrawal. Its entries hold functions, so they never travel to the
 * browser as props: the layout passes `MarketingTagIds` instead. Each tag's
 * script lives beside the loader, `TrackingConsentScripts`, in a map keyed
 * the same way. A tag missing from either map fails to compile.
 */
export const MARKETING_TAG_KEYS = ['meta-pixel', 'gtm', 'google-tag', 'active-campaign', 'openai-pixel'] as const;

export type MarketingTagKey = (typeof MARKETING_TAG_KEYS)[number];

/**
 * A tenant's id for each Marketing Tag, as the layout passes them to the
 * browser. Every field is required, so a new one cannot be left out of
 * `readMarketingTagIds`.
 */
export type MarketingTagIds = {
  metaPixelId: string | null;
  gtmId: string | null;
  googleTagId: string | null;
  activeCampaignAccountId: string | null;
  openAiPixelId: string | null;
};

/**
 * The stored id cleaned for the page, or `null` when there is none or nothing
 * usable is left of it. `unknown`, because a value written outside Payload may
 * not be the string the types promise (`storedIdText`).
 */
const cleanedId = (stored: unknown, sanitize: (value: string) => string): string | null =>
  sanitize(storedIdText(stored)) || null;

/**
 * The Tenant fields `readMarketingTagIds` reads, as a Payload `select`, for a
 * caller that reads the tenant itself. A tag whose id field is not listed
 * here fails to compile in `readMarketingTagIds`, so no reader misses it.
 */
export const MARKETING_TAG_ID_SELECT = {
  metaPixelId: true,
  gtmId: true,
  googleTagId: true,
  activeCampaignAccountId: true,
  openAiPixelId: true,
} as const satisfies Partial<Record<keyof Tenant, true>>;

type MarketingTagIdField = keyof typeof MARKETING_TAG_ID_SELECT;

/**
 * Each id is cleaned here, not only where its script is written, so an id
 * with nothing usable left counts as no Marketing Tag and cannot raise the
 * banner alone.
 */
export const readMarketingTagIds = (tenant: Pick<Tenant, MarketingTagIdField>): MarketingTagIds => ({
  metaPixelId: cleanedId(tenant.metaPixelId, sanitizeMetaPixelId),
  gtmId: cleanedId(tenant.gtmId, sanitizeGtmId),
  googleTagId: cleanedId(tenant.googleTagId, sanitizeGoogleTagId),
  activeCampaignAccountId: cleanedId(tenant.activeCampaignAccountId, sanitizeActiveCampaignAccountId),
  openAiPixelId: cleanedId(tenant.openAiPixelId, sanitizeOpenAiPixelId),
});

/**
 * What a Marketing Tag leaves in the visitor's browser, all of it cleared on
 * Withdrawal. Cookies are deleted on the site's host and every parent domain,
 * since the vendors write theirs on the registrable domain (`.example.co.uk`).
 * A name ending in `*` matches every name that starts with what precedes it.
 */
export type MarketingTagStorage = {
  cookies: readonly string[];
  localStorage?: readonly string[];
  sessionStorage?: readonly string[];
};

export type MarketingTag = {
  /** The tenant's id for this tag, or `null` when the tenant has not set it up. */
  id: (ids: MarketingTagIds) => string | null;
  /** Required, so a new tag cannot ship without saying what Withdrawal clears. */
  stores: MarketingTagStorage;
};

export const MARKETING_TAGS: Record<MarketingTagKey, MarketingTag> = {
  'meta-pixel': {
    id: ({ metaPixelId }) => metaPixelId,
    // `_fbleid` and `multiFbc` come from fbevents.js itself; Meta documents only `_fbp`/`_fbc`.
    stores: { cookies: ['_fbp', '_fbc', '_fbleid'], localStorage: ['multiFbc'] },
  },
  gtm: {
    id: ({ gtmId }) => gtmId,
    // gtm.js stores nothing itself. Withdrawal clears what every known tag
    // stores whatever loaded it, so Google's, Meta's, OpenAI's and
    // ActiveCampaign's tags inside a container are cleared through their own
    // entries; anything else a container's tags store is not, as the GTM
    // field's description tells the tenant. A Marketing Platform added for GTM
    // declarations alone, with no tag of its own, lists what its scripts store
    // here (ADR-0004).
    stores: { cookies: [] },
  },
  'google-tag': {
    id: ({ googleTagId }) => googleTagId,
    stores: {
      cookies: ['_ga', '_ga_*', '_gid', '_gat*', '_gac_*', '_gcl_*'],
      localStorage: ['_gcl_ls'],
    },
  },
  'active-campaign': {
    id: ({ activeCampaignAccountId }) => activeCampaignAccountId,
    // `prism_<accountId>`.
    stores: { cookies: ['prism_*'] },
  },
  'openai-pixel': {
    id: ({ openAiPixelId }) => openAiPixelId,
    // `__oaiq_consent` and `oaiq_consent` are the SDK's record of the consent
    // the pixel grants as it starts; cleared too, since the pixel grants it
    // again whenever it next loads. `__oaiq_domain_probe` lives a minute.
    stores: {
      cookies: ['__oppref', '__obref', '__oaiq_consent', '__oaiq_domain_probe'],
      localStorage: ['oaiq_consent'],
      sessionStorage: ['oaiq_cs:*'],
    },
  },
};

/** Whether the tenant has set up any Marketing Tag, so the cookie banner and Cookie Settings show. */
export const hasMarketingTag = (ids: MarketingTagIds) =>
  MARKETING_TAG_KEYS.some((key) => MARKETING_TAGS[key].id(ids) !== null);
